import { DONT_NEED, FILED_EXTERNALLY } from "constants/cartItemDeletionReasons";
import {
  UploadOrLinkMode,
  UploadOrLinkModeControl,
} from "constants/booksHandoff";
import { AddressAutofill } from "./addressAutofill";
import { BankAutofill } from "./bankAutofill";
import { PersonaAutofill } from "./personaAutofill";
import { Shareholder } from "./Shareholders";

export type TagType = {
  uuid: string;
  title: string;
  color_hex: string;
  bg_color_hex: string;
  tag_category: string;
};

const autofillDetailKeys = [
  "address_details",
  "persona_details",
  "bank_details",
] as const;
export type AutofillDetailKey = (typeof autofillDetailKeys)[number];

export type RepeatableField = {
  id: number;
  fields: SectionField[];
  is_address_data_missing_for_repeatable_fields_0: boolean;
  is_address_selected_for_repeatable_fields_0: boolean;
  is_persona_data_missing_for_repeatable_fields_0: boolean;
  is_persona_selected_for_repeatable_fields_0: boolean;
  selected_autofill_details?: {
    address_details: AddressAutofill;
    persona_details: PersonaAutofill;
    bank_details: BankAutofill;
    shareholder_details: Shareholder;
    persona_autofill_string: string;
    address_autofill_string: string;
  };
  group_header?: string | null;
  radio?: Record<string, string>;
  [key: string]: unknown;
};

export type RepeatableFields = RepeatableField[];

export type SectionField = {
  type: string;
  regex: string;
  value: string | null;
  options: any[];
  tooltip: string;
  disabled: boolean;
  field_key: string;
  mandatory: boolean;
  field_label: string;
  placeholder: string;
  collapse_section: boolean;
  autofill_prefix: string;
  autofill_key?: string;
  required: boolean;
  alllow_single_upload: boolean;
  note?: string;
  error_message?: string;
  validation_key?: string;
  radio?: Record<string, string>;
  template_url?: string;
  template_label?: string;
  show_description_before?: string;
  show_description_after?: string;
  is_first_column?: boolean;
  is_second_column?: boolean;
  is_hidden_for_customer?: boolean;
  should_hide_section_field?: boolean;
  description?: string;
  tag?: string;
  document?: Record<string, unknown>;
  documents?: unknown[];
  cta?: { cta_action: string };
  has_access?: boolean;
  control_basetask?: string[];
  control_basetask_for?: "CUSTOMER" | "ADMIN";
  control_basetask_show?: boolean;
  should_show_on_profit?: boolean;
  is_hidden_for_admin?: boolean;
  decimal_scale?: number;
  allow_negative?: boolean;
  max_digit_limit?: number;
  thousand_separator?: string;
  suffix?: string;
  allow_single_upload?: boolean;
  headers?: string[];
  autofill_section?: string;
  autofill_detail_key?: string;
  creatable?: boolean;
  sub_section?: string | null;
  mode?: UploadOrLinkMode;
  mode_control?: UploadOrLinkModeControl;
  link?: string;
  link_placeholder?: string;
};

export type SubSection = {
  key: string;
  title: string;
};

export type Section = {
  section_key: string;
  is_removable: boolean;
  section_title: string;
  section_fields: SectionField[];
  repeatable_fields?: RepeatableFields;
  section_comment: string;
  section_description: string;
  default_section_field: any;
  is_hidden_for_customer: boolean;
  section_duplication_allowed: boolean;
  is_bank_account_selected?: boolean;
  is_bank_account_data_missing?: boolean;
  is_address_selected?: boolean;
  is_address_data_missing?: boolean;
  is_persona_selected?: boolean;
  is_persona_data_missing?: boolean;
  selected_address_autofill_details?: {
    address_details: AddressAutofill;
  };
  selected_autofill_details?: {
    bank_details?: BankAutofill;
    address_details?: AddressAutofill;
    persona_details?: PersonaAutofill;
    shareholder_details?: Shareholder;
    designation?: string;
  };
  tooltip?: string;
  version: string;
  sub_sections?: SubSection[];
  required_on_completion?: boolean;
};

export type DirectorsSection = Section & {
  repeatable_fields: RepeatableFields;
  should_hide_repeatable_fields: boolean;
};

export type FilingFormType = {
  uuid: string;
  title: string;
  data: {
    data: {
      address_autofill_version?: string;
      section_group: {
        sections: (Section | DirectorsSection)[];
      }[];
    };
    uuid: string;
    title: string;
    version: string;
  };
};

export const FIELD_TYPE = {
  SELECT_SHAREHOLDER_AUTOFILL: "select_shareholder_autofill",
  SELECT_BANK_AUTOFILL: "select_bank_autofill",
  SELECT_AUTOFILL: "select_autofill",
  SELECT_DIRECTORS_AUTOFILL: "select_directors_autofill",
  SELECT_AUTHORIZED_SIGNATORY_AUTOFILL: "select_authorized_signatory_autofill",
  SELECT_PRINCIPAL_OFFICER_AUTOFILL: "select_principal_officer_autofill",
  SELECT_PERSONA_AUTOFILL: "select_persona_autofill",
  MULTISELECT: "multiselect",
  RADIO: "radio",
  PASSWORD: "password",
  INFO: "info",
  FILE_UPLOAD: "file_upload",
  DATE: "date",
  EMAIL: "email",
  TEXT: "text",
  TEXTAREA: "textarea",
  BOOKS_HANDOFF_SUMMARY: "books_handoff_summary",
  UPLOAD_OR_LINK: "upload_or_link",
  ACCESS_REQUEST_STATUS: "access_request_status",
  NUMBER: "number",
  AMOUNT: "amount",
  PHONE: "phone",
  NON_US_ENTITY_SELECT: "non_us_entity_select",
  CHECKBOX: "checkbox",
  SELECT: "select",
} as const;

export type AddFilingToCartPayload = {
  entity_id?: string;
  product_id: string;
  product_content_type_id: number;
  season?: string | null | number;
  quantity?: number;
  ra_state_name?: string;
  payment_amount?: number;
  state_id?: string;
  from_date?: string;
  accounting_method?: string;
  tier_id?: string;
  quarter?: string;
  deadline_date?: string;
  cart_item_id?: string;
  parent_template?: string;
  task_name?: string;
  to_date?: string;
  delete_reason?: typeof DONT_NEED | typeof FILED_EXTERNALLY;
  file?: File | null;
};
